<template>
  <b-container v-if="modelAccess === 'denied'" fluid>
    <b-alert show variant="danger">
      <translate>You don't have permission to access this section.</translate>
    </b-alert>
  </b-container>
  <!-- Rows are fetched by b-table's provider, so nothing is requested until access is granted -->
  <b-container v-else-if="this.type && modelAccess === 'granted'" fluid>
    <h1 v-if="title !== false">
      {{title || $appConfig.models[this.type].title}}
      <b-button v-if="$appConfig.models[this.type].allowNew === true && !$appConfig.models[this.type].shared || $appConfig.models[this.type].shared && isSuperAdmin" variant="secondary" size="sm" :to="'/new/' + type" v-translate>+ NEW</b-button>
      <b-button v-if="$appConfig.models[this.type].allowBulk" variant="secondary" size="sm" :to="'/bulk/' + type" v-translate>Manage Bulk</b-button>
      <b-button v-if="type === 'companies' && (isAdmin || isAccountant)" variant="primary" size="sm" :disabled="reportRequestInFlight" @click.prevent="generateReport" v-translate>
        Generate Report
      </b-button>
      <b-button @click.prevent="downloadCsv" v-if="$appConfig.models[this.type].asyncCsvReport" v-translate>Download CSV Report</b-button>
    </h1>
    <b-alert
      :show="dismissCountDown"
      dismissible
      variant="success"
      @dismissed="dismissCountDown=0; deleted = false"
      @dismiss-count-down="countDownChanged"
    >
      <span v-if="!deleted" v-translate>
        The Item has been created
      </span>
      <span v-else v-translate>
        The Item has been deleted
      </span>
    </b-alert>
    <list-filters
      v-if="!staticItems && quickFilters"
      ref="listingFilters"
      :type="type"
      :quick-filters="quickFilters"
      :pre-filters="preFilters"
      :legacy-saved-key="legacySavedKey"
      @filtering="onFiltering"
    />
    <b-row v-if="!staticItems && !quickFilters">
      <b-col md="8" class="my-1">
        <listing-filter :filter-fields="filterFields" :pre-filters="preFilters" @refresh="refreshTable" :type="type" ref="listingFilters" :withReset="false" :withSave="false" />
      </b-col>
      <b-col md="4" class="my-1">
        <b-form-group horizontal :label="$gettext('Per page')" :label-cols="9" class="text-right mb-0">
          <b-form-select :options="pageOptions" v-model="perPage" @change="onFiltered" />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="bg-white py-1 total-items-col" v-translate="{totalRows: totalRows || '-'}">
        Total items: <strong>%{totalRows}</strong>
      </b-col>
      <b-col v-if="!staticItems && quickFilters" cols="12" md="4" class="bg-white py-1">
        <b-form-group horizontal :label="$gettext('Per page')" :label-cols="9" class="text-right mb-0">
          <b-form-select :options="pageOptions" v-model="perPage" @change="onFiltered" />
        </b-form-group>
      </b-col>
    </b-row>
    <component
      v-if="listExtras"
      :is="listExtras"
      :selected="selected"
      :current-filters="filters"
      :total-rows="totalRows"
      :type="type"
      @refresh="refreshTable(filters)"
      @clear-selection="clearSelected"
      @select-all="selectAllRows"
    ></component>
    <b-table
      show-empty
      :empty-text="$gettextInterpolate($gettext('There are no %{type} to show'), {type: $appConfig.models[this.type].title})"
      id="apiListTable"
      ref="apiListTable"
      stacked="md"
      foot-clone
      :total-rows="totalRows"
      :sort-desc="sortDesc"
      :sort-by="sortBy"
      :per-page="perPage"
      :current-page="currentPage"
      :busy.sync="isBusy"
      :items="myProvider"
      :fields="fields"
      :selectable="allowSelect"
      select-mode="multi"
      selected-variant="secondary"
      @row-selected="onRowSelected"
      :api-url="this.$appConfig.models[this.type].endpoint ? this.$appConfig.api.baseUrl + this.$appConfig.models[this.type].endpoint : null"
      :filter="applyFilter"
      @filtered="onFiltered"
      @sort-changed="onFiltered"
    >

      <div slot="table-busy" class="text-center text-danger my-2">
        <b-spinner class="align-middle"></b-spinner>
        <strong v-translate>Loading...</strong>
      </div>

      <template #cell()="data">
        <component
          v-if="cellComponent(data)"
          :is="cellComponent(data).is"
          v-bind="cellComponent(data).props"
          v-on="cellComponent(data).on || {}"
        ></component>
        <div v-else-if="fields.find(x => x.key === data.field.key).filter" v-html="$options.filters[fields.find(x => x.key === data.field.key).filter](data.value)"></div>
        <div v-else-if="fields.find(x => x.key === data.field.key).formatter" v-html="data.value"></div>
        <div v-else-if="fields.find(x => x.key === data.field.key).options && fields.find(x => x.key === data.field.key).include && fields.find(x => x.key === data.field.key).showField">{{fields.find(x => x.key === data.field.key).include.findIndex(x => x[fields.find(x => x.key === data.field.key).showKey || 'id'] === data.value) !== -1 ? fields.find(x => x.key === data.field.key).include.find(x => x[fields.find(x => x.key === data.field.key).showKey || 'id'] === data.value)[fields.find(x => x.key === data.field.key).showField] : ''}}</div>
        <div v-else>{{data.value}}</div>
      </template>

      <template v-slot:cell(id)="data">
        <component
          v-if="cellComponent(data)"
          :is="cellComponent(data).is"
          v-bind="cellComponent(data).props"
          v-on="cellComponent(data).on || {}"
        ></component>
        <div v-else-if="$appConfig.models[type].noIdLink || (!$appConfig.models[type].noIdLink && $appConfig.models[type].shared === true && !isMoova) || (!$appConfig.models[type].noIdLink && $appConfig.models[type].shared === 'semi' && !isSuperAdmin)">
          {{data.value | uuid}}
        </div>
        <div v-else>
          <a :href="'/edit/' + type + '/' + data.value">
            {{data.value | uuid}}
          </a>
          <a href="#" @click.prevent="copyToClipboard(data.value)" class="text-secondary" :title="$gettext('Copy to Clipboard')">
            <font-awesome-icon :icon="['far', 'copy']" size="1x" />
          </a>
        </div>
      </template>

      <template v-slot:cell(name)="data">
        <component
          v-if="cellComponent(data)"
          :is="cellComponent(data).is"
          v-bind="cellComponent(data).props"
          v-on="cellComponent(data).on || {}"
        ></component>
        <span v-else :class="data.item.is_dangerous ? 'text-danger' : ''">{{data.value}}</span>
      </template>

      <template v-slot:cell(is_dangerous)="data">
        <span v-if="data.value" class="text-danger" v-translate>YES</span>
        <span v-else v-translate>NO</span>
      </template>

      <template v-slot:cell(from_contact)="row">
        <strong>{{row.item.from_contact}}</strong><br>
        <span v-if="row.item.from_phone"><a :href="'tel:' + row.item.from_phone">{{row.item.from_phone}}</a><br></span>
        <span v-if="row.item.from_email">{{row.item.from_email}}</span>
      </template>

      <template v-slot:cell(to_contact)="row">
        <strong>{{row.item.to_contact}}</strong><br>
        <span v-if="row.item.to_phone"><a :href="'tel:' + row.item.to_phone">{{row.item.to_phone}}</a><br></span>
        <span v-if="row.item.to_email">{{row.item.to_email}}</span>
      </template>

      <template v-slot:cell(from_address)="row">
        <span v-if="(!row.item.fields_revision || row.item.fields_revision === false) || !row.item.fields_revision_data.from || typeof row.item.fields_revision_data.from === 'object'">
          <span
            class="shipping-address-listings"
            :class="row.item.fields_revision_data &&
              row.item.fields_revision_data.from &&
              typeof row.item.fields_revision_data.from === 'object' ? 'text-warning' : ''"
            :title="row.item.address_from.formatted_address"><font-awesome-icon v-if="row.item.fields_revision_data &&
              row.item.fields_revision_data.from &&
              typeof row.item.fields_revision_data.from === 'object'" icon="exclamation-triangle" size="1x" /> {{row.item.address_from.formatted_address}}</span>
        </span>
        <span v-if="row.item.fields_revision_data !== null && typeof row.item.fields_revision_data.from === 'string'">
          <span
            class="shipping-address-listings text-danger"
            :title="row.item.fields_revision_data.from"><font-awesome-icon icon="exclamation-triangle" size="1x" /> {{row.item.fields_revision_data.from}}</span>
        </span>
        <span class="shipping-address-listings" style="display: block;" :title="row.item.from_meta" v-if="row.item.from_meta"><br>{{row.item.from_meta}}</span>
      </template>

      <template v-slot:cell(to_address)="row">
        <span v-if="(!row.item.fields_revision || row.item.fields_revision === false) || !row.item.fields_revision_data.to || typeof row.item.fields_revision_data.to === 'object'">
          <span
            class="shipping-address-listings"
            :class="row.item.fields_revision_data &&
              row.item.fields_revision_data.to &&
              typeof row.item.fields_revision_data.to === 'object' ? 'text-warning' : ''"
            :title="row.item.address_to.formatted_address"><font-awesome-icon v-if="row.item.fields_revision_data &&
              row.item.fields_revision_data.to &&
              typeof row.item.fields_revision_data.to === 'object'" icon="exclamation-triangle" size="1x" /> {{row.item.address_to.formatted_address}}</span>
        </span>
        <span v-if="row.item.fields_revision_data !== null && typeof row.item.fields_revision_data.to === 'string'">
          <span class="shipping-address-listings text-danger" :title="row.item.fields_revision_data.to"><font-awesome-icon icon="exclamation-triangle" size="1x" /> {{row.item.fields_revision_data.to}}</span>
        </span>
        <span class="shipping-address-listings" style="display: block;" :title="row.item.to_meta" v-if="row.item.to_meta">{{row.item.to_meta}}</span>
      </template>

      <template v-slot:cell(scheduled_date)="row">
        <div v-if="row.item.scheduled_date">
          {{moment.utc(row.item.scheduled_date).tz($tz).format('YYYY-MM-DD')}}<br>
          {{moment.utc(row.item.scheduled_date).tz($tz).format('HH:mm:ss')}}
        </div>
      </template>

      <template v-slot:cell(updated_at)="row">
        {{moment.utc(row.item.updated_at).tz($tz).format('YYYY-MM-DD')}}<br>
        {{moment.utc(row.item.updated_at).tz($tz).format('HH:mm:ss')}}
      </template>

      <template v-slot:cell(created_at)="row">
        {{moment.utc(row.item.created_at).tz($tz).format('YYYY-MM-DD')}}<br>
        {{moment.utc(row.item.created_at).tz($tz).format('HH:mm:ss')}}
      </template>

      <template v-slot:cell(tags)="row">
        <div v-if="row.item.tags.length > 0" style="width: 220px;">
          <span v-for="(item, index) in row.item.tags" :key="index">
            <b-badge pill variant="danger">{{ item.name }}</b-badge>&nbsp;
          </span>
        </div>
      </template>

      <template v-slot:cell(courier)="row">
        <div v-if="row.item.courier">
          <user-card :user='row.item.courier' :profile-picture="false" :with-titles="false" :vehicle-types-prop="vehicleTypes" />
        </div>
      </template>

      <template v-slot:cell(phone)="row">
        <component
          v-if="cellComponent(row)"
          :is="cellComponent(row).is"
          v-bind="cellComponent(row).props"
          v-on="cellComponent(row).on || {}"
        ></component>
        <span v-else>{{row.item.phone | phone(row.item.country_iso_code || 'ar')}}</span>
      </template>

      <template v-slot:cell(actions)="row">
        <component
          v-if="cellComponent(row)"
          :is="cellComponent(row).is"
          v-bind="cellComponent(row).props"
          v-on="cellComponent(row).on || {}"
        ></component>
        <template v-else>
          <span v-if="$appConfig.models[type].extraActions && (!action.position || action.position !== 'end') && (typeof action.visibilityCondition === 'undefined' || action.visibilityCondition(row.item))" v-for="(action, index) in $appConfig.models[type].extraActions" :key="index">
            <b-button
              :size="action.size || 'sm'"
              :variant="action.variant || 'primary'"
              @click="action.action(row.item)"
              :title="action.title || ''"
              style="padding: .5rem .65rem"
              class="mb-1"
              :disabled="(action.disabledCondition && action.disabledCondition(row.item))"
            >
              <font-awesome-icon v-if="action.icon" :icon="action.icon" size="2x" style="font-size: 16px;vertical-align: middle" />
              <span v-else>{{action.label}}</span>
            </b-button>
          </span>
          <b-button size="sm" variant="primary" @click.stop="editingRow = JSON.parse(JSON.stringify(row))" v-b-modal="'modalEditInPlace' + type" style="padding: .5rem .6rem" class="mb-1" v-if="editInPlace">
            <font-awesome-icon icon="edit" size="2x" style="font-size: 16px;vertical-align: middle" />
          </b-button>
          <b-button size="sm" @click.stop="row.toggleDetails" style="padding: .5rem" class="mb-1">
            <font-awesome-icon v-if="row.detailsShowing" :icon="['far', 'eye-slash']" size="2x" style="font-size: 16px;vertical-align: middle" />
            <font-awesome-icon v-else :icon="['far', 'eye']" size="2x" style="font-size: 16px;vertical-align: middle" />
          </b-button>
          <b-button size="sm" variant="danger" @click.stop="deleteRow(row.item.id)" style="padding: .5rem .63rem" class="mb-1" v-if="allowDelete && !$appConfig.models[type].shared || (allowDelete && $appConfig.models[type].shared && isSuperAdmin)">
            <font-awesome-icon icon="trash-alt" size="2x" style="font-size: 16px;vertical-align: middle" />
          </b-button>
          <span v-if="$appConfig.models[type].extraActions && action.position === 'end'" v-for="(action, index) in $appConfig.models[type].extraActions" :key="index" class="d-inline-block">
            <b-button
              :size="action.size || 'sm'"
              :variant="action.variant || 'primary'"
              @click="action.action(row.item)"
              :title="action.title || ''"
              style="padding: .5rem .65rem"
              class="mb-1"
              :disabled="(action.disabledCondition && action.disabledCondition(row.item))"
            >
              <font-awesome-icon v-if="action.icon" :icon="action.icon" size="2x" style="font-size: 16px;vertical-align: middle" />
              <span v-else>{{action.label}}</span>
            </b-button>&nbsp;
          </span>
        </template>
      </template>

      <template v-slot:row-details="row">
        <b-card>
          <ul>
            <li v-for="(value, key) in row.item" :key="key" v-if="value && value.length !== 0 && value !== 'undefined' && value !== null">{{ key }}: <span v-html="key.indexOf('phone') === -1 ? isJson(value) : value.toString()"></span></li>
          </ul>
        </b-card>
      </template>

    </b-table>
    <b-pagination :total-rows="totalRows" :per-page="perPage" v-model="currentPage" v-if="totalRows > perPage" class="my-0" />

    <b-modal :id="'modalEditInPlace' + type" :ref="'modalEditInPlace' + type" hide-footer size="lg">
      <div slot="modal-title" v-translate="{type: $appConfig.models[this.type].title}">Edit %{type}</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <api-item-form :id="editingRow.item.id" :type="this.type" :static-item="editingRow" v-if="editingRow.item" @success="updateInPlace" />
    </b-modal>

  </b-container>
</template>

<script>
import ListingFilter from '@/components/Engine/ListingFilter'
import ListFilters from '@/components/Engine/ListFilters'
import UserCard from '@/components/UserCard'
import ApiItemForm from '@/components/Engine/ApiItemForm'
import { getCountries, getCountry } from '@/services/country'
import { toFiltersParam } from '@/common/apiFilters'
import { resolveModelAccess } from '@/common/modelAccess'

export default {
  name: 'ApiList',
  components: {
    ListingFilter,
    ListFilters,
    UserCard,
    ApiItemForm
  },
  props: {
    type: {
      type: String,
      required: true
    },
    title: {
      type: [String, Boolean],
      default: null
    },
    preFilters: {
      type: Array,
      default: () => {
        return []
      }
    },
    prePerPage: {
      type: Number,
      default: 25
    },
    staticItems: {
      type: Array,
      default: null
    },
    noActions: {
      type: Boolean,
      default: false
    },
    quickFilters: {
      type: Object,
      default: null
    },
    permanentFilters: {
      type: Array,
      default: () => {
        return []
      }
    },
    extraParams: {
      type: Object,
      default: null
    },
    transformItems: {
      type: Function,
      default: null
    },
    legacySavedKey: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      fields: [],
      filterFields: this.$appConfig.models[this.type].filters,
      exclude: this.$appConfig.models[this.type].exclude,
      includes: this.$appConfig.models[this.type].includes || [],
      allowDelete: this.$appConfig.models[this.type].allowDelete,
      isBusy: false,
      currentPage: 1,
      perPage: this.prePerPage,
      totalRows: null,
      pageOptions: [ 10, 25, 50, 100, 500 ],
      sortBy: 'updated_at',
      sortDesc: true,
      sortDirection: 'desc',
      filters: this.preFilters,
      dismissSecs: 5,
      dismissCountDown: 0,
      deleted: false,
      editingRow: {},
      csvUrl: {},
      selected: [],
      extraFilterGroups: [],
      suppressCountrize: false,
      // Con manualApply no se dispara ningún request hasta el primer evento
      // filtering (botón Apply, ?qf=/?s= en la URL o saved search)
      deferInitialFetch: Boolean(this.quickFilters && this.quickFilters.manualApply)
    }
  },
  computed: {
    modelAccess() {
      return resolveModelAccess(this, this.$appConfig.models[this.type])
    },
    applyFilter() {
      return this.filter
    },
    editInPlace() {
      return this.$appConfig.models[this.type].editInPlace || false
    },
    allowSelect() {
      return this.$appConfig.models[this.type].allowSelect === true
    },
    listExtras() {
      return this.$appConfig.models[this.type].listExtrasComponent || null
    }
  },
  watch: {
    type: async function (val) {
      this.resetData()
      this.deferInitialFetch = Boolean(this.quickFilters && this.quickFilters.manualApply)
      // Absent when the previous type's list was not rendered (access denied or still pending)
      if (this.$refs.listingFilters) {
        this.$refs.listingFilters.resetData()
      }
      this.fields = this.$appConfig.models[this.type].fields
      this.filterFields = this.$appConfig.models[this.type].filters
      this.exclude = this.$appConfig.models[this.type].exclude
      if (!this.fields.some(x => x.key === 'actions')) {
        this.fields.push({ key: 'actions', label: this.$gettext('Actions') })
      }
      const that = this
      this.fields = this.fields.filter(function (obj) {
        if (that.exclude) {
          return that.exclude.indexOf(obj.key) === -1
        } else {
          return true
        }
      })
      for (const field of this.fields) {
        if (field.options && !Array.isArray(field.options)) {
          await this['fetch' + field.options.charAt(0).toUpperCase() + field.options.slice(1)]()
          field.include = this[field.options]
          // field.includeSelect = this[field.options + 'Select']
        }
      }
      this.refreshTable()
    },
    exclude: function (val) {
      return this.$appConfig.models[this.type].exclude
    }
  },
  created() {
    this.fetchVehicleTypes()
  },
  async mounted() {
    this.fetchVehicleTypes()
    this.resetData()
    if (this.$route.query.created === true) {
      this.showAlert()
    }
    if (this.$route.query.deleted === true) {
      this.deleted = true
      this.showAlert()
    }
    this.fields = this.$appConfig.models[this.type].fields
    this.includes = this.$appConfig.models[this.type].includes
    this.exclude = this.$appConfig.models[this.type].exclude
    const that = this
    const fields = this.fields.filter(function (obj) {
      if (that.exclude) {
        return that.exclude.indexOf(obj.key) === -1
      } else {
        return true
      }
    })
    const hasModelActions = fields.some(x => x.key === 'actions')
    if (!hasModelActions) {
      fields.push({ key: 'actions', label: this.$gettext('Actions') })
      if (this.noActions || this.$appConfig.models[this.type].noActions) {
        fields.pop()
      }
    }
    this.fields = fields
    for (const field of this.fields) {
      if (field.options && !Array.isArray(field.options)) {
        await this['fetch' + field.options.charAt(0).toUpperCase() + field.options.slice(1)]()
        field.include = this[field.options]
        // field.includeSelect = this[field.options + 'Select']
      }
    }
    this.refreshTable()
  },
  methods: {
    cellComponent(data) {
      const field = this.fields.find(x => x.key === data.field.key)
      return field && field.component ? field.component(data.value, data.field.key, data.item, data) : null
    },
    onRowSelected(rows) {
      this.selected = rows
      this.$emit('selectionChanged', rows)
    },
    selectAllRows() {
      this.$refs.apiListTable.selectAllRows()
    },
    clearSelected() {
      this.$refs.apiListTable.clearSelected()
    },
    generateReport() {
      const params = {
        format: 'upload',
        filter_groups: []
      }
      if (!this.$cookies.get('bypassCountry') && this.$appConfig.models[this.type].countrize) {
        if (Array.isArray(this.$appConfig.models[this.type].countrize)) {
          const filters = this.$appConfig.models[this.type].countrize.map(x => {
            return [x, 'eq', getCountry()]
          })
          params.filter_groups.push(
            {
              or: true,
              filters: [
                ...filters
              ]
            }
          )
        } else {
          params.filter_groups.push(
            {
              filters: [
                [this.$appConfig.models[this.type].countrize, 'eq', getCountry()]
              ]
            }
          )
        }
      }
      if (!this.$cookies.get('bypassCountry') && this.$appConfig.models[this.type].countrizeById) {
        params.filter_groups.push(
          {
            filters: [
              [this.$appConfig.models[this.type].countrizeById, 'eq', getCountries().find(x => x.iso_code === getCountry()).id]
            ]
          }
        )
      }
      params.filters = toFiltersParam(params.filter_groups)
      delete params.filter_groups
      return this.generateReportAsync({url: this.$appConfig.api.baseUrl + this.type, params})
    },
    myProvider (ctx) {
      if (this.deferInitialFetch) {
        return []
      }
      if (!this.staticItems) {
        const params = {
          page: ctx.currentPage - 1,
          limit: ctx.perPage,
          includes: this.includes,
          sort: [
            {
              key: ctx.sortBy || 'id',
              direction: ctx.sortDesc ? 'desc' : 'asc'
            }
          ],
          filter_groups: [],
          ...(this.extraParams || {})
        }
        if (this.filters || this.preFilters.length > 0) {
          params.filter_groups = [
            {
              filters: this.filters || this.preFilters
            }
          ]
        }

        if (this.extraFilterGroups.length > 0) {
          params.filter_groups.push(...this.extraFilterGroups)
        }

        if (this.permanentFilters.length > 0) {
          params.filter_groups.push({ filters: this.permanentFilters })
        }

        if (!this.$cookies.get('bypassCountry') && !this.suppressCountrize && this.$appConfig.models[this.type].countrize) {
          if (Array.isArray(this.$appConfig.models[this.type].countrize)) {
            const filters = this.$appConfig.models[this.type].countrize.map(x => {
              return [x, 'eq', getCountry()]
            })
            params.filter_groups.push(
              {
                or: true,
                filters: [
                  ...filters
                ]
              }
            )
          } else {
            params.filter_groups.push(
              {
                filters: [
                  [this.$appConfig.models[this.type].countrize, 'eq', getCountry()]
                ]
              }
            )
          }
        }
        if (!this.$cookies.get('bypassCountry') && !this.suppressCountrize && this.$appConfig.models[this.type].countrizeById) {
          params.filter_groups.push(
            {
              filters: [
                [this.$appConfig.models[this.type].countrizeById, 'eq', getCountries().find(x => x.iso_code === getCountry()).id]
              ]
            }
          )
        }

        params.filters = toFiltersParam(params.filter_groups)
        delete params.filter_groups

        const csvParams = {...params}
        csvParams.format = 'csv'

        this.csvUrl = {
          url: ctx.apiUrl,
          params: csvParams
        }

        const promise = this.$http.get(ctx.apiUrl, { params })

        return promise.then((data) => {
          let items = data.body.data // .filter(x => x.meta.ai.addresses.to.aiAddress.confidence < 70)
          if (this.transformItems) {
            items = this.transformItems(items)
          }
          // // Here we could override the busy state, setting isBusy to false
          // // this.isBusy = false
          this.totalRows = data.body.pagination.total // items.length
          this.$emit('itemsLoaded', items)

          this.$emit('totalRows', this.totalRows)
          return (items)
        }).catch(error => {
          console.error(error)
          // Here we could override the busy state, setting isBusy to false
          // this.isBusy = false
          // Returning an empty array, allows table to correctly handle busy state in case of error
          return []
        })
      } else {
        const promise = Promise.resolve(this.staticItems)
        this.totalRows = this.staticItems.length
        this.$emit('itemsLoaded', this.staticItems)
        this.$emit('totalRows', this.totalRows)
        return promise.then(data => {
          return data
        })
      }
    },
    refreshTable (filters) {
      this.filters = filters
      setTimeout(() => {
        this.$root.$emit('bv::refresh::table', 'apiListTable')
      }, 100)
    },
    onFiltering (payload) {
      this.deferInitialFetch = false
      this.extraFilterGroups = payload.groups || []
      this.suppressCountrize = Boolean(payload.suppressCountrize)
      this.currentPage = 1
      this.refreshTable(payload.filters)
    },
    onFiltered (filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length
      this.currentPage = 1
      this.refreshTable(this.filters)
    },
    deleteRow(id) {
      if (
        confirm(this.$gettextInterpolate(this.$gettext('Are you sure you want to DELETE the row with ID:\n%{id}\nWARNING: THIS ACTION CANNOT BE UNDONE!'), {id}))
      ) {
        const endpoint = this.$appConfig.models[this.type].endpointDelete ? this.$appConfig.models[this.type].endpointDelete.replace('{id}', id) : this.$appConfig.models[this.type].endpoint + '/' + id
        this.$http.delete(this.$appConfig.api.baseUrl + endpoint)
          .then(response => {
            this.refreshTable(this.filters)
          })
      }
    },
    updateInPlace(data) {
      if (!this.staticItems) {
        this.refreshTable(this.filters)
      } else {
        this.staticItems[data.index] = data.item
        this.editingRow = {}
        this.refreshTable(this.filters)
      }
      if (this.$appConfig.models[this.type].editInPlace) {
        this.$refs['modalEditInPlace' + this.type].hide()
      }
    },
    downloadCsv() {
      const data = {
        url: this.csvUrl.url,
        params: this.csvUrl.params,
        type: 'text/csv;charset=utf-8;',
        filename: this.moment().local().format('YYYY-MM-DD_HH-mm') + '__' + this.type + '_export'
      }
      this.downloadAsync(data)
    },
    resetData: function () {
      Object.assign(this.$data, this.$options.data.call(this))
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
    showAlert() {
      this.dismissCountDown = this.dismissSecs
    }
  }
}
</script>

<style scoped>
/* Centra verticalmente el "Total items" cuando comparte fila con el
   selector de per-page; el margin repone el espacio que flex colapsa
   entre el texto y el <strong> (no se puede envolver en un span sin
   cambiar el msgid de vue-gettext) */
.total-items-col {
  display: flex;
  align-items: center;
}
/* ::v-deep: v-translate re-inyecta el innerHTML en runtime y el strong
   pierde el atributo de scope */
.total-items-col ::v-deep strong {
  margin-left: 0.3em;
}
</style>
